import _ from "lodash";
/* external */

/* internal */
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import {
  ALPHA,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  DEPENDENT,
  GRADE_VALUE,
  MIDDLE,
  NUMERIC,
  NUMERIC_VALUE,
  REGULAR,
  SPECIAL,
  MYP_FINAL_GRADE,
  DP_IB_DEFINED,
  MYP_CRITERIA_TOTAL,
} from "Constants/stringConstants";
import { generateRandomIdV2 } from "Utils";
import {
  DP_GRADE_SCALE_OPTIONS,
  MYP_GRADE_SCALE_OPTIONS,
} from "./GradeScalesConstants";
import { getShouldHideDefaultLevelInGradeScale } from "../../../modules/Utils";

const DEFAULT = "DEFAULT";

export const VALIDATION_TYPES = {
  EMPTY: "EMPTY",
  DUPLICATE: "DUPLICATE",
  RANGE: "RANGE",
  ORDER: "ORDER",
  REQUIRED: "REQUIRED",
  LAST_ROW_ZERO: "LAST_ROW_ZERO",
  GRADE_VALUE_CUTOFF_MISMATCH: "GRADE_VALUE_CUTOFF_MISMATCH",
  MASTERY_LEVEL_REQUIRED: "MASTERY_LEVEL_REQUIRED",
};

export const ERROR_MESSAGES = {
  abbreviation: {
    [VALIDATION_TYPES.EMPTY]: "Abbreviation is mandatory",
    [VALIDATION_TYPES.DUPLICATE]: "Abbreviation label must be unique",
  },
  cutoff: {
    [VALIDATION_TYPES.EMPTY]: "Cutoff % is mandatory",
    [VALIDATION_TYPES.DUPLICATE]: "Cutoff % should be unique for each grade",
    [VALIDATION_TYPES.RANGE]: "Cutoff % must be between 100 and 0",
    [VALIDATION_TYPES.ORDER]: (prevValue, nextValue) =>
      `Cutoff % must be between ${prevValue} and ${nextValue}`,
    [VALIDATION_TYPES.LAST_ROW_ZERO]:
      "Cutoff % for the lowest level should be ‘0 or higher’",
  },
  gradeValue: {
    [VALIDATION_TYPES.EMPTY]: "Grade value is mandatory",
    [VALIDATION_TYPES.DUPLICATE]: "Grade value should be unique for each grade",
    [VALIDATION_TYPES.RANGE]: "Grade value must be between 100 and 0",
    [VALIDATION_TYPES.ORDER]: (prevValue, nextValue) =>
      `Grade value must be between ${prevValue} and ${nextValue}`,
    [VALIDATION_TYPES.GRADE_VALUE_CUTOFF_MISMATCH]:
      "Grade value for the highest level cannot be less than the cutoff %",
  },
  dependentValues: {
    [VALIDATION_TYPES.EMPTY]: "At least one grade must be mapped",
  },
  masteryLevel: {
    [VALIDATION_TYPES.MASTERY_LEVEL_REQUIRED]:
      "At least one grade must be marked as mastery level",
  },
};

export const updateDisplaySequenceForGradeScaleValues = values =>
  _.map(values, (item, index) => ({
    ...item,
    displaySequence: index,
  }));

const hasValueChanged = (oldValue, newValue) => !_.isEqual(oldValue, newValue);

const collectErrorMessages = validationFields => {
  const errors = new Set();

  _.forEach(validationFields.fieldErrors, (value, key) => {
    if (!key.startsWith("row_")) {
      const message = getErrorMessage(key, value);
      if (message) errors.add(message);
    }
  });

  _.forEach(validationFields.fieldErrors, (rowErrors, key) => {
    if (key.startsWith("row_")) {
      _.forEach(rowErrors, (errorType, field) => {
        const message = getErrorMessage(field, errorType);
        if (message) errors.add(message);
      });
    }
  });

  return Array.from(errors);
};

const validateGradeValue = (
  currentGradeValue,
  rowErrors,
  gradeValuesSet,
  prevValue,
  nextValue,
  index,
  totalLength,
  cutoffValue
) => {
  if (_.isNil(currentGradeValue) || currentGradeValue === "") {
    rowErrors.gradeValue = VALIDATION_TYPES.EMPTY;
    return;
  }

  if (gradeValuesSet.has(currentGradeValue)) {
    rowErrors.gradeValue = VALIDATION_TYPES.DUPLICATE;
    return;
  }
  gradeValuesSet.add(currentGradeValue);

  if (
    !_.isNumber(currentGradeValue) ||
    currentGradeValue < 0 ||
    currentGradeValue > 100
  ) {
    rowErrors.gradeValue = VALIDATION_TYPES.RANGE;
    return;
  }

  // Validation for highest level (first row): Grade value cannot be less than cutoff%
  if (
    index === 0 &&
    _.isNumber(cutoffValue) &&
    currentGradeValue < cutoffValue
  ) {
    rowErrors.gradeValue = VALIDATION_TYPES.GRADE_VALUE_CUTOFF_MISMATCH;
    return;
  }

  // ORDER validation: gradeValue should be between prevValue and nextValue (for middle rows)
  if (index !== 0 && index !== totalLength - 1) {
    if (currentGradeValue >= prevValue || currentGradeValue <= nextValue) {
      rowErrors.gradeValue = {
        type: VALIDATION_TYPES.ORDER,
        prevValue,
        nextValue,
      };
    }
  }
};

const validateCutoffValue = (
  currentMinValue,
  rowErrors,
  prevValue,
  nextValue,
  validationFields,
  index,
  totalLength
) => {
  if (validationFields.minValues.has(currentMinValue)) {
    rowErrors.cutoff = VALIDATION_TYPES.DUPLICATE;
  } else {
    validationFields.minValues.add(currentMinValue);

    if (
      !_.isNumber(currentMinValue) ||
      currentMinValue < 0 ||
      currentMinValue > 100
    ) {
      rowErrors.cutoff = VALIDATION_TYPES.RANGE;
    } else if (index === totalLength - 1 && currentMinValue !== 0) {
      rowErrors.cutoff = VALIDATION_TYPES.LAST_ROW_ZERO;
    } else if (index !== 0 && index !== totalLength - 1) {
      if (currentMinValue >= prevValue || currentMinValue <= nextValue) {
        rowErrors.cutoff = {
          type: VALIDATION_TYPES.ORDER,
          prevValue: prevValue,
          nextValue: nextValue,
        };
      }
    }
  }
};

const validateDependentGradeScaleRow = (
  value,
  rowErrors,
  selectedDependentGradeScales,
  allGradeScaleValues,
  totalRowCount
) => {
  const isSpecialGradeTemplateRow =
    _.get(value, "type", "REGULAR") === "SPECIAL";

  if (isSpecialGradeTemplateRow) {
    return;
  }

  const dependentValues = _.get(value, "dependentGradeScaleValues", []);

  // Validate each selected dependent grade scale
  _.forEach(selectedDependentGradeScales, gradeScaleId => {
    const valuesForGradeScale = _.filter(
      dependentValues,
      item => _.get(item, "gradeScale.id") === gradeScaleId
    );

    // Check if at least one valid value exists for this grade scale
    const hasValidValue = _.some(
      valuesForGradeScale,
      item => !_.isEmpty(_.trim(_.get(item, "abbreviation", "")))
    );

    // Calculate available values for this grade scale
    const availableValuesCount = _.size(
      _.chain(allGradeScaleValues)
        .filter(gsValue => _.get(gsValue, "gradeScaleId") === gradeScaleId)
        .flatMap(gsValue => _.get(gsValue, "values", []))
        .filter(val => _.get(val, "type") !== "SPECIAL")
        .value()
    );

    if (totalRowCount > availableValuesCount) {
      // Allow empty rows when there aren't enough values for all rows
      return;
    }

    if (_.isEmpty(valuesForGradeScale) || !hasValidValue) {
      if (!rowErrors.dependentValues) rowErrors.dependentValues = {};
      rowErrors.dependentValues[gradeScaleId] = VALIDATION_TYPES.EMPTY;
    }
  });
};

const calculateMaxValue = ({
  gradeScaleValuesData,
  currentIndex,
  currentItemId,
  subjectLevelId,
}) => {
  const index = currentItemId
    ? _.findIndex(gradeScaleValuesData, { id: currentItemId })
    : currentIndex;

  let maxValue = 100;
  if (index > 0) {
    const prevRow = gradeScaleValuesData[index - 1];
    const prevScore = _.find(_.get(prevRow, "gradeScaleValueScores", []), s => {
      const scoreSubjectLevelId = _.get(s, "subjectLevel.id", null);

      // Normalize both values to use "DEFAULT" instead of null
      const normalizedScoreId = _.isNil(scoreSubjectLevelId)
        ? DEFAULT
        : scoreSubjectLevelId;
      const normalizedSubjectLevelId =
        _.isNil(subjectLevelId) || subjectLevelId === "null"
          ? DEFAULT
          : subjectLevelId;

      return _.isEqual(normalizedScoreId, normalizedSubjectLevelId);
    });

    maxValue = _.get(prevScore, "minValue", 100);
  }
  return maxValue;
};

const shouldIncludeGradeScaleValueScore = ({
  score,
  differentGradebookValuesForLevels,
  shouldHideDefaultLevel,
}) => {
  if (!differentGradebookValuesForLevels) {
    return _.isNil(_.get(score, "subjectLevel.id", null));
  }
  if (shouldHideDefaultLevel) {
    return !_.isNil(_.get(score, "subjectLevel.id", null));
  }
  return true;
};

const createGradeValueCalculateTypeDetail = ({
  gradeScaleSettings,
  differentGradebookValuesForLevels,
  shouldHideDefaultLevel,
}) => {
  const result = differentGradebookValuesForLevels
    ? _.map(_.get(gradeScaleSettings, "gradeValueMethod"), item => ({
        subjectLevelId: _.get(item, "subjectLevel.id", null),
        gradeValueCalculateType: _.get(item, "gradeValueCalculateType"),
      }))
    : {
        gradeValueCalculateType: _.get(
          gradeScaleSettings,
          "gradeValueMethod",
          "MIDDLE"
        ),
      };

  return shouldHideDefaultLevel && differentGradebookValuesForLevels
    ? _.filter(result, item => !_.isNil(_.get(item, "subjectLevelId", null)))
    : result;
};

const processGradeScaleValue = ({
  value,
  index,
  gradeScaleType,
  gradeScaleValuesData,
  basicDetails,
  shouldHideDefaultLevel,
  differentGradebookValuesForLevels,
}) => {
  const type = _.get(value, "type", "REGULAR");
  const gradeScaleValueObject = {
    abbreviation: _.get(value, "abbreviation", ""),
    label: _.get(value, "label", ""),
    description: _.get(value, "description", ""),
    color: _.get(value, "color", "") || "var(--green-600)",
    displaySequence: index,
    type,
    ...(type === "SPECIAL"
      ? { presetValueId: _.get(value, "presetValueId") }
      : {}),
  };

  if (gradeScaleType === REGULAR) {
    const isMasteryEnabled = _.get(basicDetails, "isUseForMastery") === "yes";
    const filteredGradeScaleValueScores = _.filter(
      _.get(value, "gradeScaleValueScores"),
      score =>
        shouldIncludeGradeScaleValueScore({
          score,
          differentGradebookValuesForLevels,
          shouldHideDefaultLevel,
        })
    );

    const gradeScaleValueScores = _.map(
      filteredGradeScaleValueScores,
      (score, scoreIndex) => {
        const subjectLevelId = _.get(score, "subjectLevel.id", null);

        // Normalize subjectLevelId to "DEFAULT" for calculateMaxValue
        const normalizedSubjectLevelId =
          _.isNil(subjectLevelId) || subjectLevelId === "null"
            ? DEFAULT
            : subjectLevelId;

        const maxValue = calculateMaxValue({
          gradeScaleValuesData,
          currentIndex: index,
          subjectLevelId: normalizedSubjectLevelId,
        });

        const scoreObject = {
          subjectLevelId,
          minValue: _.get(score, "minValue"),
          maxValue,
          gradeValue: _.get(score, "gradeValue"),
        };

        // Only include isMasteryLevel if mastery is enabled
        if (isMasteryEnabled) {
          scoreObject.isMasteryLevel = _.get(score, "isMasteryLevel");
        }

        return scoreObject;
      }
    );

    // Add gradeScaleValueGradePoints for credit/GPA calculation
    const isUseForCreditGPACalculation =
      _.get(basicDetails, "isUseForCreditGPACalculation") === "yes";
    const gradePointItem = _.first(
      _.get(value, "gradeScaleValueGradePoints", [])
    );
    const rawGradePoint = _.get(gradePointItem, "gradePoint");
    const gradePointValue = _.toNumber(rawGradePoint);
    const gradeScaleValueGradePoints =
      isUseForCreditGPACalculation && gradePointItem
        ? {
            ...(rawGradePoint !== "" && _.isFinite(gradePointValue)
              ? { gradePoint: gradePointValue }
              : {}),
            isAddedValueApplicable: _.get(
              gradePointItem,
              "isAddedValueApplicable",
              false
            ),
          }
        : undefined;

    return {
      ...gradeScaleValueObject,
      gradeScaleValueScores,
      ...(isUseForCreditGPACalculation && gradeScaleValueGradePoints
        ? { gradeScaleValueGradePoints }
        : {}),
    };
  }

  if (gradeScaleType === DEPENDENT) {
    const dependentGradeScaleValueIds = _.chain(
      _.get(value, "dependentGradeScaleValues", [])
    )
      .filter(item =>
        _.includes(
          _.get(basicDetails, "gradeScalesToConvert", []),
          _.get(item, "gradeScale.id")
        )
      )
      .map(item => _.get(item, "id"))
      .value();

    return { ...gradeScaleValueObject, dependentGradeScaleValueIds };
  }

  return gradeScaleValueObject;
};

const getGradeScaleValueGradePoints = ({ state, newItem }) => {
  const isUseForCreditGPACalculation =
    _.get(state, "basicDetails.isUseForCreditGPACalculation") === "yes";
  if (!isUseForCreditGPACalculation) return {};

  const gradePointItem = _.first(
    _.get(newItem, "gradeScaleValueGradePoints", [])
  );
  if (!gradePointItem) return {};

  const rawGradePoint = _.get(gradePointItem, "gradePoint");
  const gradePointValue = _.toNumber(rawGradePoint);
  const gradeScaleValueGradePoints = {
    ...(rawGradePoint !== "" && _.isFinite(gradePointValue)
      ? { gradePoint: gradePointValue }
      : {}),
    isAddedValueApplicable: _.get(
      gradePointItem,
      "isAddedValueApplicable",
      false
    ),
  };

  if (!gradeScaleValueGradePoints.gradePoint) return {};

  return { gradeScaleValueGradePoints };
};

const createGradeScaleValueFields = ({
  gradeScaleType,
  gradeScaleSettings,
  differentGradebookValuesForLevels,
  basicDetails,
  shouldHideDefaultLevel,
}) => {
  const gradeScaleValuesData = _.get(
    gradeScaleSettings,
    "gradeScaleValuesData",
    []
  );
  const result = {
    gradeScaleValues: _.chain(gradeScaleValuesData)
      .map((value, index) =>
        processGradeScaleValue({
          value,
          index,
          gradeScaleType,
          gradeScaleValuesData,
          basicDetails,
          shouldHideDefaultLevel,
          differentGradebookValuesForLevels,
        })
      )
      .compact()
      .value(),
  };

  if (gradeScaleType === REGULAR) {
    result.gradeValueCalculateTypeDetail = createGradeValueCalculateTypeDetail({
      gradeScaleSettings,
      differentGradebookValuesForLevels,
      shouldHideDefaultLevel,
    });
  }

  return result;
};

export const transformGradeScaleData = ({
  state,
  curriculumId,
  curriculumType,
  allLevels = [],
}) => {
  const gradeScaleType = _.get(state, "gradeScaleType");
  const basicDetails = _.get(state, "basicDetails", {});
  const templateUid = _.get(basicDetails, "templateId");
  const gradeScaleSettings = _.get(state, "gradeScaleSettings", {});
  const differentGradebookValuesForLevels =
    _.get(state, "basicDetails.differentGradebookValuesForLevels") === "yes";
  // when levels are enabled, always use all available levels to avoid stale selectedLevels
  const selectedLevels = differentGradebookValuesForLevels
    ? _.map(allLevels, "id")
    : _.get(state, "basicDetails.selectedLevels", []);
  const shouldHideDefaultLevel = getShouldHideDefaultLevelInGradeScale({
    levels: selectedLevels,
    curriculumType,
    differentGradebookValuesForLevels,
  });

  const basicDetailsFields = {
    templateName: _.get(basicDetails, "templateName", ""),
    templateUid: _.isEmpty(templateUid)
      ? "GS_" + generateRandomIdV2({ length: 4 })
      : templateUid,
    displayName: _.get(basicDetails, "displayName", ""),
    description: _.get(basicDetails, "templateDescription", ""),
    type: gradeScaleType,
    specialGradeTemplateId: _.get(basicDetails, "specialGradeTemplate", ""),
    gradingType:
      gradeScaleType === SPECIAL ? null : _.get(basicDetails, "type", ALPHA),
    isUseForCreditGPACalculation:
      _.get(basicDetails, "isUseForCreditGPACalculation") === "yes",
    isUseForMastery: _.get(basicDetails, "isUseForMastery") === "yes",
    ...(gradeScaleType === REGULAR && {
      calculateStandardGradesUsing:
        _.get(basicDetails, "type", ALPHA) === NUMERIC
          ? _.get(basicDetails, "calculateStandardGradesUsing", NUMERIC_VALUE)
          : GRADE_VALUE,
    }),
    minValue:
      _.get(basicDetails, "type", ALPHA) === NUMERIC
        ? parseFloat(_.get(basicDetails, "minValue"))
        : null,
    maxValue:
      _.get(basicDetails, "type", ALPHA) === NUMERIC
        ? parseFloat(_.get(basicDetails, "maxValue"))
        : null,
    dependentGradeScaleIds:
      gradeScaleType === DEPENDENT
        ? _.get(basicDetails, "gradeScalesToConvert", null)
        : null,
    ...(differentGradebookValuesForLevels || !_.isEmpty(selectedLevels)
      ? {
          metadata: {
            ...(differentGradebookValuesForLevels && {
              differentGradebookValuesForLevels,
            }),
            ...(!_.isEmpty(selectedLevels) && {
              selectedLevels,
            }),
          },
        }
      : {}),
  };

  const gradeScaleValueFields = createGradeScaleValueFields({
    gradeScaleType,
    gradeScaleSettings,
    differentGradebookValuesForLevels,
    basicDetails,
    shouldHideDefaultLevel,
  });

  return {
    input: {
      entityType: "CURRICULUM_PROGRAM",
      entityDetail: {
        curriculumProgramId: curriculumId,
      },
      ...basicDetailsFields,
      ...gradeScaleValueFields,
    },
  };
};

export const getBulkImportGradeScaleVariables = ({
  state,
  gradeScaleId,
  curriculumId,
  academicYearId,
  academicTermsData,
  isImportToAllEntities,
}) => {
  const isCourseBasedGrading = getIsCourseBasedGradingEnabled();
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  let bulkImportEntityDetails = [],
    targetEntityType;

  if (isLearningCoursesEnabled && isCourseBasedGrading) {
    const selectedCourses = _.get(state, "selectedCourses", []);
    const selectedGradingPeriods = _.get(state, "selectedGradingPeriods", {});

    targetEntityType = "LEARNING_COURSE";

    bulkImportEntityDetails = _.chain(selectedCourses)
      .map(courseId => {
        const gradingPeriods = _.get(selectedGradingPeriods, courseId, []);

        let shouldAddToBulkImport = !_.isEmpty(gradingPeriods);

        if (isImportToAllEntities) {
          shouldAddToBulkImport =
            shouldAddToBulkImport &&
            _.size(gradingPeriods) !== _.size(academicTermsData); // only add if specifically edited terms, by default it'll be copied to all terms;
        }

        if (shouldAddToBulkImport) {
          return {
            learningCourseId: courseId,
            academicTermIds: _.map(gradingPeriods, "id"),
          };
        }
        return null;
      })
      .compact()
      .value();
  } else {
    const bulkApplyToSubjectsData = _.get(state, "bulkApplyToSubjects", {});

    bulkImportEntityDetails = _.flatMap(
      bulkApplyToSubjectsData,
      (subjectData, subjectId) => {
        const subjectGradeIds = _.get(subjectData, "gradeIds", []);
        const gradingPeriodIds = _.get(subjectData, "gradingPeriodIds", []);

        return _.map(subjectGradeIds, gradeId => ({
          subjectId,
          gradeId,
          academicTermIds: gradingPeriodIds,
        }));
      }
    );
  }

  if (!isImportToAllEntities && _.isEmpty(bulkImportEntityDetails)) {
    return null;
  }

  return {
    input: {
      gradeScaleIds: [gradeScaleId],
      sourceEntityDetails: {
        entityType: "CURRICULUM_PROGRAM",
        entityId: curriculumId,
      },
      targetEntityType,
      academicYearId,
      isImportToAllEntities,
      bulkImportEntityDetails,
    },
  };
};

export const getInitialGradeScaleState = (levels = [], editData = null) => {
  if (!editData) {
    return {};
  }

  const gradeScaleData = _.get(editData, "gradeScaleData", {});
  const gradeScaleType = _.get(editData, "gradeScaleType");

  const gradeScaleEntityDetails = _.get(
    gradeScaleData,
    "gradeScaleEntityDetails[0]",
    {}
  );
  const gradeScaleValues = _.get(
    gradeScaleEntityDetails,
    "gradeScaleValues",
    []
  );

  const differentGradebookValuesForLevels = _.get(
    gradeScaleData,
    "metadata.differentGradebookValuesForLevels",
    false
  );

  const selectedLevels = _.get(gradeScaleData, "metadata.selectedLevels", []);

  const basicDetailsFields = {
    templateName: _.get(gradeScaleData, "templateName"),
    templateId: _.get(gradeScaleData, "templateUid"),
    displayName: _.get(gradeScaleData, "displayName"),
    type: _.get(gradeScaleData, "gradingType", ""),
    templateDescription: _.get(gradeScaleData, "description"),
    isUseForMastery: _.get(gradeScaleData, "isUseForMastery") ? "yes" : "no",
    calculateStandardGradesUsing: _.get(
      gradeScaleData,
      "calculateStandardGradesUsing",
      "GRADE_VALUE"
    ),
    minValue: _.get(gradeScaleData, "minValue"),
    maxValue: _.get(gradeScaleData, "maxValue"),
    isUseForCreditGPACalculation: _.get(
      gradeScaleData,
      "isUseForCreditGPACalculation"
    )
      ? "yes"
      : "no",
    specialGradeTemplate: _.get(gradeScaleData, "specialGradeTemplate.id", ""),
    gradeScalesToConvert: _.map(
      _.get(gradeScaleData, "dependentGradeScales", []),
      "id"
    ),
    differentGradebookValuesForLevels: differentGradebookValuesForLevels
      ? "yes"
      : "no",
    selectedLevels: selectedLevels,
  };

  return {
    gradeScaleType,
    id: _.get(editData, "id"),
    basicDetails: basicDetailsFields,
    subjectLevelIds: _.map(levels, item => _.get(item, "id")),
    gradeScaleSettings: {
      gradeValueMethod: differentGradebookValuesForLevels
        ? _.map(
            _.get(gradeScaleEntityDetails, "gradeValueCalculateTypeDetail"),
            item => ({
              subjectLevel: {
                id: _.get(item, "subjectLevel.id", null),
              },
              gradeValueCalculateType: _.get(
                item,
                "gradeValueCalculateType",
                MIDDLE
              ),
            })
          )
        : _.get(
            gradeScaleEntityDetails,
            "gradeValueCalculateTypeDetail[0].gradeValueCalculateType",
            MIDDLE
          ),

      gradeScaleValuesData: gradeScaleValues,
    },
    selectedCourses: [],
    selectedGradingPeriods: [],
    isImportToAllEntities: false,
    editData: gradeScaleData,
  };
};

// --- Helper utils for comparing fields ---
const getBasicDetailsUpdates = (
  originalData,
  state,
  gradeScaleType,
  allLevels = []
) => {
  const updates = {};
  const basicDetails = _.get(state, "basicDetails", {});
  if (hasValueChanged(originalData.templateName, basicDetails.templateName)) {
    updates.templateName = basicDetails.templateName;
  }
  if (hasValueChanged(originalData.displayName, basicDetails.displayName)) {
    updates.displayName = basicDetails.displayName;
  }
  if (hasValueChanged(originalData.templateUid, basicDetails.templateId)) {
    updates.templateUid = basicDetails.templateId;
  }
  if (
    hasValueChanged(originalData.description, basicDetails.templateDescription)
  ) {
    updates.description = basicDetails.templateDescription;
  }
  if (hasValueChanged(originalData.gradingType, basicDetails.type)) {
    updates.gradingType = basicDetails.type;
  }
  if (
    gradeScaleType === REGULAR &&
    hasValueChanged(
      originalData.isUseForMastery,
      basicDetails.isUseForMastery === "yes"
    )
  ) {
    updates.isUseForMastery = basicDetails.isUseForMastery === "yes";
  }

  if (
    _.includes([REGULAR, DP_IB_DEFINED], gradeScaleType) &&
    hasValueChanged(
      _.get(originalData, "metadata.differentGradebookValuesForLevels"),
      basicDetails.differentGradebookValuesForLevels === "yes"
    )
  ) {
    _.set(
      updates,
      "metadata.differentGradebookValuesForLevels",
      basicDetails.differentGradebookValuesForLevels === "yes"
    );
  }
  // when levels enabled, always sync to all available levels to avoid stale selectedLevels
  const effectiveSelectedLevels =
    basicDetails.differentGradebookValuesForLevels === "yes"
      ? _.map(allLevels, "id")
      : basicDetails.selectedLevels;
  if (
    _.includes([REGULAR, DP_IB_DEFINED], gradeScaleType) &&
    hasValueChanged(
      _.get(originalData, "metadata.selectedLevels"),
      effectiveSelectedLevels
    )
  ) {
    _.set(updates, "metadata.selectedLevels", effectiveSelectedLevels);
  }
  if (gradeScaleType === REGULAR) {
    // Handle calculateStandardGradesUsing only for REGULAR grade scales and NUMERIC type else set to GRADE_VALUE
    if (
      _.get(basicDetails, "type", ALPHA) === NUMERIC &&
      hasValueChanged(
        _.get(originalData, "calculateStandardGradesUsing"),
        basicDetails.calculateStandardGradesUsing
      )
    ) {
      updates.calculateStandardGradesUsing =
        basicDetails.calculateStandardGradesUsing;
    } else if (_.get(basicDetails, "type", ALPHA) === ALPHA) {
      updates.calculateStandardGradesUsing = GRADE_VALUE;
    }
  }
  if (
    gradeScaleType === REGULAR &&
    hasValueChanged(_.get(originalData, "minValue"), basicDetails.minValue)
  ) {
    updates.minValue = Number(basicDetails.minValue);
  }
  if (
    gradeScaleType === REGULAR &&
    hasValueChanged(_.get(originalData, "maxValue"), basicDetails.maxValue)
  ) {
    updates.maxValue = Number(basicDetails.maxValue);
  }
  if (
    gradeScaleType === REGULAR &&
    hasValueChanged(
      originalData.isUseForCreditGPACalculation,
      basicDetails.isUseForCreditGPACalculation === "yes"
    )
  ) {
    updates.isUseForCreditGPACalculation =
      basicDetails.isUseForCreditGPACalculation === "yes";
  }
  return updates;
};

const getSpecialGradeTemplateUpdates = (
  originalData,
  state,
  gradeScaleType
) => {
  const updates = {};
  const prevSpecialGradesTemplateId = _.get(
    originalData,
    "specialGradeTemplate.id"
  );
  const updatedSpecialGradesTemplateId = _.get(
    state,
    "basicDetails.specialGradeTemplate"
  );
  if (
    gradeScaleType !== SPECIAL &&
    hasValueChanged(prevSpecialGradesTemplateId, updatedSpecialGradesTemplateId)
  ) {
    if (updatedSpecialGradesTemplateId) {
      updates.addSpecialGradeTemplateId = updatedSpecialGradesTemplateId;
    } else {
      updates.removeSpecialGradeTemplateId = prevSpecialGradesTemplateId;
    }
  }
  return updates;
};

const getDependentGradeScalesUpdates = (
  originalData,
  state,
  gradeScaleType
) => {
  const updates = {};
  const prevDependentIds = _.map(
    _.get(originalData, "dependentGradeScales", []),
    "id"
  );
  const updatedDependentIds = _.get(
    state,
    "basicDetails.gradeScalesToConvert",
    []
  );
  if (gradeScaleType === DEPENDENT) {
    const addDependentGradeScaleIds = _.difference(
      updatedDependentIds,
      prevDependentIds
    );
    const removeDependentGradeScaleIds = _.difference(
      prevDependentIds,
      updatedDependentIds
    );
    if (!_.isEmpty(addDependentGradeScaleIds)) {
      updates.addDependentGradeScaleIds = addDependentGradeScaleIds;
    }
    if (!_.isEmpty(removeDependentGradeScaleIds)) {
      updates.removeDependentGradeScaleIds = removeDependentGradeScaleIds;
    }
  }
  return updates;
};

const getGradeScaleValuesUpdates = (
  originalData,
  state,
  gradeScaleType,
  isAddingSpecialGradeTemplate,
  isSpecialGradeTemplateChanged
) => {
  const updates = {};
  const oldGradeScaleEntityDetails = _.get(
    originalData,
    "gradeScaleEntityDetails",
    []
  );
  const oldGradeScaleValues = _.get(
    oldGradeScaleEntityDetails[0],
    "gradeScaleValues",
    []
  );
  const newGradeScaleValues = _.get(
    state,
    "gradeScaleSettings.gradeScaleValuesData",
    []
  );
  const oldValueMap = _.keyBy(oldGradeScaleValues, "id");
  const newValueMap = _.keyBy(newGradeScaleValues, "id");

  // Check if mastery is being disabled
  const oldMasteryEnabled = _.get(originalData, "isUseForMastery", false);
  const newMasteryEnabled =
    _.get(state, "basicDetails.isUseForMastery") === "yes";
  const isMasteryBeingDisabled = oldMasteryEnabled && !newMasteryEnabled;

  // Removed
  const removeGradeScaleValues = _.chain(oldGradeScaleValues)
    .filter(
      oldItem =>
        !newValueMap[oldItem.id] &&
        !(
          !isSpecialGradeTemplateChanged &&
          _.get(oldItem, "type", "REGULAR") === "SPECIAL"
        )
    )
    .map("id")
    .value();
  if (!_.isEmpty(removeGradeScaleValues)) {
    updates.removeGradeScaleValues = removeGradeScaleValues;
  }

  // Added
  const addGradeScaleValues = _.chain(newGradeScaleValues)
    .filter(
      newItem =>
        (newItem.id && newItem.id.startsWith("NEW_")) ||
        (isAddingSpecialGradeTemplate &&
          _.get(newItem, "type", "REGULAR") === "SPECIAL")
    )
    .sortBy("displaySequence")
    .map(newItem => {
      const isSpecialGrade = _.get(newItem, "type", "REGULAR") === "SPECIAL";
      return {
        abbreviation: newItem.abbreviation,
        label: newItem.label,
        description: newItem.description,
        color: _.get(newItem, "color", "") || "var(--green-600)",
        type: _.get(newItem, "type", "REGULAR"),
        displaySequence: newItem.displaySequence,
        ...(isSpecialGrade
          ? {
              presetValueId: _.get(newItem, "presetValueId"),
            }
          : {}),
        ...(gradeScaleType === REGULAR && {
          gradeScaleValueScores: _.map(newItem.gradeScaleValueScores, score => {
            const subjectLevelId = _.get(score, "subjectLevel.id", null);
            const isMasteryEnabled =
              _.get(state, "basicDetails.isUseForMastery") === "yes";

            // Normalize subjectLevelId to "DEFAULT" for calculateMaxValue
            const normalizedSubjectLevelId = _.isNil(subjectLevelId)
              ? DEFAULT
              : subjectLevelId;

            const maxValue = calculateMaxValue({
              gradeScaleValuesData: newGradeScaleValues,
              currentItemId: newItem.id,
              subjectLevelId: normalizedSubjectLevelId,
            });

            const scoreObject = {
              minValue: parseFloat(score.minValue) || 0,
              maxValue,
              gradeValue: parseFloat(score.gradeValue) || 0,
              subjectLevelId: _.isNil(subjectLevelId) ? null : subjectLevelId,
            };

            // Only include isMasteryLevel if mastery is enabled
            if (isMasteryEnabled) {
              scoreObject.isMasteryLevel = score.isMasteryLevel;
            }

            return scoreObject;
          }),
        }),
        // Add gradeScaleValueGradePoints for credit/GPA calculation
        ...getGradeScaleValueGradePoints({ state, newItem }),
        ...(gradeScaleType === DEPENDENT && {
          dependentGradeScaleValueIds: _.map(
            _.get(newItem, "dependentGradeScaleValues", []),
            "id"
          ),
        }),
      };
    })
    .value();
  if (!_.isEmpty(addGradeScaleValues)) {
    updates.addGradeScaleValues = addGradeScaleValues;
  }

  // Updated
  const updateGradeScaleValues = _.chain(newGradeScaleValues)
    .filter(newItem => {
      const oldItem = oldValueMap[newItem.id];
      if (!oldItem || newItem.id.startsWith("NEW_")) return false;
      if (
        hasValueChanged(oldItem.abbreviation, newItem.abbreviation) ||
        hasValueChanged(oldItem.label, newItem.label) ||
        hasValueChanged(oldItem.description, newItem.description) ||
        hasValueChanged(oldItem.color, newItem.color) ||
        hasValueChanged(oldItem.displaySequence, newItem.displaySequence)
      ) {
        return true;
      }

      // For REGULAR grade scales: compare gradeScaleValueScores
      // For DEPENDENT grade scales:  this section is skipped and logic continues from line 931 where dependentGradeScaleValues are compared
      // For SPECIAL grade scales: the basic field comparisons above are sufficient
      if (gradeScaleType === REGULAR) {
        const oldScores = _.get(oldItem, "gradeScaleValueScores", []);
        const newScores = _.get(newItem, "gradeScaleValueScores", []);
        if (oldScores.length !== newScores.length) return true;
        // Normalize subjectLevelId to "DEFAULT" for calculateMaxValue
        const oldScoreMap = _.keyBy(oldScores, s => {
          const id = _.get(s, "subjectLevel.id", null);
          return _.isNil(id) ? DEFAULT : id;
        });
        const newScoreMap = _.keyBy(newScores, s => {
          const id = _.get(s, "subjectLevel.id", null);
          return _.isNil(id) ? DEFAULT : id;
        });
        for (const key in newScoreMap) {
          const oldScore = oldScoreMap[key];
          const newScore = newScoreMap[key];
          if (!oldScore) return true;

          const isMasteryEnabled =
            _.get(state, "basicDetails.isUseForMastery") === "yes";

          let hasChanged =
            hasValueChanged(oldScore.minValue, newScore.minValue) ||
            hasValueChanged(oldScore.maxValue, newScore.maxValue) ||
            hasValueChanged(oldScore.gradeValue, newScore.gradeValue);

          // Only check isMasteryLevel if mastery is enabled
          if (isMasteryEnabled) {
            hasChanged =
              hasChanged ||
              hasValueChanged(oldScore.isMasteryLevel, newScore.isMasteryLevel);
          }

          if (hasChanged) {
            return true;
          }
        }

        // Check for gradeScaleValueGradePoints changes
        const isUseForCreditGPACalculation =
          _.get(state, "basicDetails.isUseForCreditGPACalculation") === "yes";
        if (isUseForCreditGPACalculation) {
          const oldGradePoints = _.get(
            oldItem,
            "gradeScaleValueGradePoints",
            []
          );
          const newGradePoints = _.get(
            newItem,
            "gradeScaleValueGradePoints",
            []
          );
          if (
            hasValueChanged(
              _.get(oldGradePoints, "[0].gradePoint"),
              _.get(newGradePoints, "[0].gradePoint")
            ) ||
            hasValueChanged(
              _.get(oldGradePoints, "[0].isAddedValueApplicable"),
              _.get(newGradePoints, "[0].isAddedValueApplicable")
            )
          ) {
            return true;
          }
        }
      }

      // Compare dependentGradeScaleValues for DEPENDENT grade scales
      if (gradeScaleType === DEPENDENT) {
        const oldDepValues = _.get(oldItem, "dependentGradeScaleValues", []);
        const newDepValues = _.get(newItem, "dependentGradeScaleValues", []);
        const oldDepIds = _.map(oldDepValues, "id");
        const newDepIds = _.map(newDepValues, "id");

        if (hasValueChanged(_.sortBy(oldDepIds), _.sortBy(newDepIds))) {
          return true;
        }
      }

      return false;
    })
    .map(newItem => {
      const oldItem = oldValueMap[newItem.id];
      const updateObj = {
        id: newItem.id,
        abbreviation: newItem.abbreviation,
        label: newItem.label,
        description: newItem.description,
        displaySequence: newItem.displaySequence,
        color: _.get(newItem, "color", "") || "var(--green-600)",
      };
      if (gradeScaleType === REGULAR) {
        const oldScores = _.get(oldItem, "gradeScaleValueScores", []);
        const newScores = _.get(newItem, "gradeScaleValueScores", []);
        // Normalize subjectLevelId to "DEFAULT" for calculateMaxValue
        const oldScoreMap = _.keyBy(oldScores, s => {
          const id = _.get(s, "subjectLevel.id", null);
          return _.isNil(id) ? DEFAULT : id;
        });
        const newScoreMap = _.keyBy(newScores, s => {
          const id = _.get(s, "subjectLevel.id", null);
          return _.isNil(id) ? DEFAULT : id;
        });
        const addGradeScaleValueScores = [];
        const updateGradeScaleValueScores = [];

        _.forEach(newScoreMap, (newScore, key) => {
          const oldScore = oldScoreMap[key];
          const isMasteryEnabled =
            _.get(state, "basicDetails.isUseForMastery") === "yes";

          const maxValue = calculateMaxValue({
            gradeScaleValuesData: newGradeScaleValues,
            currentItemId: newItem.id,
            subjectLevelId: key,
          });

          if (oldScore && oldScore.id) {
            const updateScoreObject = {
              minValue: parseFloat(newScore.minValue) || 0,
              maxValue,
              gradeValue: parseFloat(newScore.gradeValue) || 0,
              id: oldScore.id,
            };

            // Handle isMasteryLevel field based on mastery enablement
            if (isMasteryEnabled) {
              updateScoreObject.isMasteryLevel = newScore.isMasteryLevel;
            } else if (isMasteryBeingDisabled) {
              // When mastery is being disabled, explicitly set isMasteryLevel to false for all existing scores
              updateScoreObject.isMasteryLevel = false;
            }

            updateGradeScaleValueScores.push(updateScoreObject);
          } else {
            const addScoreObject = {
              minValue: parseFloat(newScore.minValue) || 0,
              maxValue,
              gradeValue: parseFloat(newScore.gradeValue) || 0,
              subjectLevelId: key === DEFAULT ? null : key,
            };

            // Only include isMasteryLevel if mastery is enabled
            if (isMasteryEnabled) {
              addScoreObject.isMasteryLevel = newScore.isMasteryLevel;
            }

            addGradeScaleValueScores.push(addScoreObject);
          }
        });

        if (updateGradeScaleValueScores.length) {
          updateObj.updateGradeScaleValueScores = updateGradeScaleValueScores;
        }
        if (addGradeScaleValueScores.length) {
          updateObj.addGradeScaleValueScores = addGradeScaleValueScores;
        }

        // Add upsertGradeScaleValueGradePoints for credit/GPA calculation
        const isUseForCreditGPACalculation =
          _.get(state, "basicDetails.isUseForCreditGPACalculation") === "yes";
        if (isUseForCreditGPACalculation) {
          const newGradePointItem = _.first(
            _.get(newItem, "gradeScaleValueGradePoints", [])
          );
          const oldGradePointItem = _.first(
            _.get(oldItem, "gradeScaleValueGradePoints", [])
          );

          if (newGradePointItem) {
            const upsertGradeScaleValueGradePoints = {
              gradePoint:
                parseFloat(_.get(newGradePointItem, "gradePoint", 0)) || 0,
              isAddedValueApplicable: _.get(
                newGradePointItem,
                "isAddedValueApplicable",
                false
              ),
            };

            updateObj.upsertGradeScaleValueGradePoints = upsertGradeScaleValueGradePoints;
          }
        }
      }
      if (gradeScaleType === DEPENDENT) {
        const oldDepValues = _.get(oldItem, "dependentGradeScaleValues", []);
        const newDepValues = _.get(newItem, "dependentGradeScaleValues", []);
        const oldDepIds = _.map(oldDepValues, "id");
        const newDepIds = _.map(newDepValues, "id");
        updateObj.addDependentGradeScaleValueIds = _.difference(
          newDepIds,
          oldDepIds
        );
        updateObj.removeDependentGradeScaleValueIds = _.difference(
          oldDepIds,
          newDepIds
        );
      }
      return updateObj;
    })
    .value();
  if (!_.isEmpty(updateGradeScaleValues)) {
    updates.updateGradeScaleValues = updateGradeScaleValues;
  }
  return updates;
};

const getGradeValueCalculateTypeDetailUpdates = (
  originalData,
  state,
  gradeScaleType
) => {
  const updates = {};
  if (gradeScaleType !== REGULAR) return updates;

  const oldMethods = _.get(
    originalData,
    "gradeScaleEntityDetails[0].gradeValueCalculateTypeDetail",
    []
  );
  const newMethods = _.get(state, "gradeScaleSettings.gradeValueMethod");

  // If newMethods is an array - differentGradebookValuesForLevels = true
  if (_.isArray(newMethods)) {
    const oldMethodMap = _.keyBy(oldMethods, m =>
      _.get(m, "subjectLevel.id", null)
    );
    const updateArr = [];
    const addArr = [];

    _.forEach(newMethods, newMethod => {
      const subjectLevelId = _.get(newMethod, "subjectLevel.id", null);
      const gradeValueCalculateType = _.get(
        newMethod,
        "gradeValueCalculateType"
      );
      const old = oldMethodMap[subjectLevelId];
      if (old?.id) {
        if (old.gradeValueCalculateType !== gradeValueCalculateType) {
          updateArr.push({ id: old.id, gradeValueCalculateType });
        }
      } else if (subjectLevelId) {
        addArr.push({ subjectLevelId, gradeValueCalculateType });
      }
    });

    if (updateArr.length)
      updates.updateGradeValueCalculateTypeDetail = updateArr;
    if (addArr.length) updates.addGradeValueCalculateTypeDetail = addArr;
    return updates;
  }

  // Else - differentGradebookValuesForLevels = false
  const [oldSingleMethod] = oldMethods;
  const oldType = oldSingleMethod?.gradeValueCalculateType;
  const id = oldSingleMethod?.id;

  if (id && hasValueChanged(oldType, newMethods)) {
    updates.updateGradeValueCalculateTypeDetail = {
      id,
      gradeValueCalculateType: newMethods,
    };
  }

  return updates;
};

export const getGradeScaleUpdateInput = ({
  state,
  curriculumId,
  allLevels = [],
}) => {
  const originalData = _.get(state, "editData", {});
  if (!originalData) return null;
  const gradeScaleType = _.get(state, "gradeScaleType");
  const specialGradeTemplateUpdates = getSpecialGradeTemplateUpdates(
    originalData,
    state,
    gradeScaleType
  );
  const isAddingSpecialGradeTemplate = !_.isEmpty(
    _.get(specialGradeTemplateUpdates, "addSpecialGradeTemplateId")
  );
  const isSpecialGradeTemplateChanged = !_.isEmpty(specialGradeTemplateUpdates);

  const updates = {
    ...getBasicDetailsUpdates(originalData, state, gradeScaleType, allLevels),
    ...specialGradeTemplateUpdates,
    ...getDependentGradeScalesUpdates(originalData, state, gradeScaleType),
    ...getGradeScaleValuesUpdates(
      originalData,
      state,
      gradeScaleType,
      isAddingSpecialGradeTemplate,
      isSpecialGradeTemplateChanged
    ),
    ...getGradeValueCalculateTypeDetailUpdates(
      originalData,
      state,
      gradeScaleType
    ),
  };
  return _.isEmpty(updates)
    ? null
    : {
        input: {
          id: originalData.id,
          entityType: "CURRICULUM_PROGRAM",
          entityDetail: {
            curriculumProgramId: curriculumId,
          },
          ...updates,
        },
      };
};

export const getErrorMessage = (field, errorInfo) => {
  if (
    field === "cutoff" &&
    _.isObject(errorInfo) &&
    errorInfo.type === VALIDATION_TYPES.ORDER
  ) {
    return ERROR_MESSAGES[field][VALIDATION_TYPES.ORDER](
      errorInfo.prevValue,
      errorInfo.nextValue
    );
  }
  return _.get(ERROR_MESSAGES, `${field}.${errorInfo}`, "");
};

export const validateGradeScaleState = ({
  state,
  allGradeScalesData,
  levels,
  curriculumType,
  differentGradebookValuesForLevels,
}) => {
  const validationFields = {
    abbreviation: new Set(),
    minValues: new Set(),
    gradeValues: new Set(),
    prevGradeValue: null,
    nextGradeValue: null,
    previousMinValue: null,
    nextMinValue: null,
    fieldErrors: {},
  };

  // Validate basic details
  const basicDetails = _.get(state, "basicDetails", {});
  const gradeScaleType = _.get(state, "gradeScaleType");

  if (_.isEmpty(_.trim(_.get(basicDetails, "templateName", "")))) {
    _.set(
      validationFields,
      "fieldErrors.templateName",
      VALIDATION_TYPES.REQUIRED
    );
  }

  // Validate grade scale values
  const gradeScaleValues = _.get(
    state,
    "gradeScaleSettings.gradeScaleValuesData",
    []
  );

  if (_.isEmpty(gradeScaleValues)) {
    return {
      isValid: false,
      errors: ["At least one grade scale value is required"],
      validationFields,
    };
  }

  const getValuesForLevel = (values, levelId) => {
    // Filter out special grade templates for validation purposes
    const regularValues = _.filter(
      values,
      value => _.get(value, "type", "REGULAR") === "REGULAR"
    );

    return _.map(regularValues, value => {
      const scores = _.get(value, "gradeScaleValueScores", []);
      return _.find(
        scores,
        score => _.get(score, "subjectLevel.id", null) === levelId
      );
    });
  };

  const shouldHideDefaultLevel = getShouldHideDefaultLevelInGradeScale({
    levels,
    curriculumType,
    differentGradebookValuesForLevels,
  });

  // Validate each row
  let regularValueIndex = 0;
  _.forEach(gradeScaleValues, (value, index) => {
    const rowErrors = {};

    // abbreviation validation
    const abbreviation = _.trim(_.get(value, "abbreviation", ""));
    if (_.isEmpty(abbreviation)) {
      _.set(rowErrors, "abbreviation", VALIDATION_TYPES.EMPTY);
    } else if (validationFields.abbreviation.has(abbreviation)) {
      _.set(rowErrors, "abbreviation", VALIDATION_TYPES.DUPLICATE);
    } else {
      validationFields.abbreviation.add(abbreviation);
    }

    // Regular grade scale validations
    if (gradeScaleType === "REGULAR") {
      const isSpecialGradeTemplateRow =
        _.get(value, "type", "REGULAR") === "SPECIAL";

      if (isSpecialGradeTemplateRow) {
        return;
      }

      const gradeScaleValueScores = _.filter(
        _.get(value, "gradeScaleValueScores", []),
        score =>
          shouldIncludeGradeScaleValueScore({
            score,
            differentGradebookValuesForLevels,
            shouldHideDefaultLevel,
          })
      );

      // Validate each score within gradeScaleValueScores
      _.forEach(gradeScaleValueScores, score => {
        const subjectLevelId = _.get(score, "subjectLevel.id", null);

        const currentMinValue = _.get(score, "minValue");
        const currentGradeValue = parseFloat(_.get(score, "gradeValue"));

        // Get previous and next values for this specific level
        const levelScores = getValuesForLevel(gradeScaleValues, subjectLevelId);
        const currentLevelMinValues = _.map(levelScores, s =>
          _.get(s, "minValue")
        );
        const currentLevelGradeValues = _.map(levelScores, s =>
          parseFloat(_.get(s, "gradeValue"))
        );

        const prevMinValue =
          regularValueIndex > 0
            ? currentLevelMinValues[regularValueIndex - 1]
            : 100;
        const nextMinValue =
          regularValueIndex < currentLevelMinValues.length - 1
            ? currentLevelMinValues[regularValueIndex + 1]
            : 0;
        const prevGradeValue =
          regularValueIndex > 0
            ? currentLevelGradeValues[regularValueIndex - 1]
            : 100;
        const nextGradeValue =
          regularValueIndex < currentLevelGradeValues.length - 1
            ? currentLevelGradeValues[regularValueIndex + 1]
            : 0;

        // Create level-specific validation sets
        const levelKey = `level_${subjectLevelId || "null"}`;
        if (!validationFields[levelKey]) {
          validationFields[levelKey] = {
            minValues: new Set(),
            gradeValues: new Set(),
          };
        }

        // Grade value validation
        if (_.isNil(currentGradeValue)) {
          if (!rowErrors.gradeValue) rowErrors.gradeValue = {};
          rowErrors.gradeValue[subjectLevelId || "default"] =
            VALIDATION_TYPES.EMPTY;
        } else {
          const gradeValueErrors = {};
          validateGradeValue(
            currentGradeValue,
            gradeValueErrors,
            validationFields[levelKey].gradeValues,
            prevGradeValue,
            nextGradeValue,
            regularValueIndex,
            currentLevelGradeValues.length,
            currentMinValue
          );
          if (gradeValueErrors.gradeValue) {
            if (!rowErrors.gradeValue) rowErrors.gradeValue = {};
            rowErrors.gradeValue[subjectLevelId || "default"] =
              gradeValueErrors.gradeValue;
          }
        }

        // Cutoff (minValue) validation
        if (_.isNil(currentMinValue)) {
          if (!rowErrors.cutoff) rowErrors.cutoff = {};
          rowErrors.cutoff[subjectLevelId || "default"] =
            VALIDATION_TYPES.EMPTY;
        } else {
          const cutoffErrors = {};
          validateCutoffValue(
            currentMinValue,
            cutoffErrors,
            prevMinValue,
            nextMinValue,
            validationFields[levelKey],
            regularValueIndex,
            currentLevelMinValues.length
          );
          if (cutoffErrors.cutoff) {
            if (!rowErrors.cutoff) rowErrors.cutoff = {};
            rowErrors.cutoff[subjectLevelId || "default"] = cutoffErrors.cutoff;
          }
        }
      });

      // Increment the regular value index after processing a regular row
      regularValueIndex++;
    }

    // Dependent grade scale validations
    if (gradeScaleType === "DEPENDENT") {
      const selectedDependentGradeScales = _.get(
        basicDetails,
        "gradeScalesToConvert",
        []
      );

      const allGradeScaleValues = _.map(
        selectedDependentGradeScales,
        gradeScaleId => {
          const gradeScale = _.find(
            allGradeScalesData,
            edge => _.get(edge, "node.id") === gradeScaleId
          );

          return {
            gradeScaleId,
            values: _.get(
              gradeScale,
              "node.gradeScaleEntityDetails[0].gradeScaleValues",
              []
            ),
          };
        }
      );

      // Count non-special rows for validation
      const totalRowCount = _.size(
        _.filter(gradeScaleValues, val => _.get(val, "type") !== "SPECIAL")
      );

      validateDependentGradeScaleRow(
        value,
        rowErrors,
        selectedDependentGradeScales,
        allGradeScaleValues,
        totalRowCount
      );
    }

    if (!_.isEmpty(rowErrors)) {
      _.set(validationFields, `fieldErrors.row_${index}`, rowErrors);
    }
  });

  // Validate mastery level requirement
  const isMasteryLevelEnabled =
    _.get(basicDetails, "isUseForMastery") === "yes";

  if (gradeScaleType === "REGULAR" && isMasteryLevelEnabled) {
    const hasMasteryLevel = _.some(gradeScaleValues, value => {
      const isSpecialGradeTemplateRow =
        _.get(value, "type", "REGULAR") === "SPECIAL";

      if (isSpecialGradeTemplateRow) {
        return false;
      }

      const gradeScaleValueScores = _.get(value, "gradeScaleValueScores", []);
      return _.some(gradeScaleValueScores, score =>
        _.get(score, "isMasteryLevel", false)
      );
    });

    if (!hasMasteryLevel) {
      _.set(
        validationFields,
        "fieldErrors.masteryLevel",
        VALIDATION_TYPES.MASTERY_LEVEL_REQUIRED
      );
    }
  }

  // Check if errors are due to empty fields or validation errors
  const hasEmptyFieldErrors = _.some(
    validationFields.fieldErrors,
    (fieldError, fieldKey) => {
      // Check basic field errors (like templateName)
      if (
        _.isString(fieldError) &&
        _.includes(["EMPTY", "REQUIRED"], fieldError)
      ) {
        return true;
      }

      // Check row-level errors
      if (fieldKey.startsWith("row_")) {
        return _.some(fieldError, errorValue => {
          if (
            _.isString(errorValue) &&
            _.includes(["EMPTY", "REQUIRED"], errorValue)
          ) {
            return true;
          }
          if (_.isObject(errorValue)) {
            return _.some(errorValue, subError =>
              _.includes(["EMPTY", "REQUIRED"], subError)
            );
          }

          return false;
        });
      }

      return false;
    }
  );

  const errorMessage = hasEmptyFieldErrors
    ? "toastMsgs:please_fill_all_required_fields"
    : "toastMsgs:resolve_error_first";
  return {
    isValid: _.isEmpty(_.get(validationFields, "fieldErrors")),
    errors: collectErrorMessages(validationFields),
    errorMessage,
    validationFields,
  };
};

export const getSelectedGradeScaleOptions = ({
  selectedOptions,
  curriculumType,
}) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP: {
      if (_.includes(selectedOptions, REGULAR)) {
        return _.concat(selectedOptions, MYP_GRADE_SCALE_OPTIONS);
      }

      return selectedOptions;
    }

    case CURRICULUM_TYPE_DP: {
      if (_.includes(selectedOptions, REGULAR)) {
        return _.concat(selectedOptions, DP_GRADE_SCALE_OPTIONS);
      }

      return selectedOptions;
    }

    default: {
      return selectedOptions;
    }
  }
};

export const getFilteredGradeScalesData = ({
  allGradeScalesDataFromQuery,
  searchText,
  selectedOptions,
  curriculumType,
}) => {
  return _.filter(allGradeScalesDataFromQuery, gradeScale => {
    const gradeScaleNode = _.get(gradeScale, "node", {});
    const gradeScaleType = _.get(gradeScaleNode, "type");

    // Search text filtering - only search in templateUid, templateName, displayName
    if (!_.isEmpty(searchText)) {
      const searchFields = [
        _.get(gradeScaleNode, "templateUid", ""),
        _.get(gradeScaleNode, "templateName", ""),
        _.get(gradeScaleNode, "displayName", ""),
      ];

      const combinedText = _.join(searchFields, " ");
      const matchesSearch = _.includes(
        _.toLower(combinedText),
        _.toLower(searchText)
      );

      if (!matchesSearch) {
        return false;
      }
    }

    // Type filtering
    if (_.isEmpty(selectedOptions)) {
      return true;
    }

    const selectedTypes = getSelectedGradeScaleOptions({
      selectedOptions,
      curriculumType,
    });

    return _.includes(selectedTypes, gradeScaleType);
  });
};

export const handleSpecialGradeScaleChanges = ({
  currentGradeScaleValuesData,
  specialGradesTemplateId,
  specialGradeScale,
  isUpdatingExistingTemplate,
  gradeScaleType,
}) => {
  // Skip if conditions not met
  if (
    (isUpdatingExistingTemplate && _.isEmpty(specialGradesTemplateId)) ||
    _.isEmpty(gradeScaleType)
  ) {
    return currentGradeScaleValuesData;
  }

  // Filter out existing special grades
  const newGradeScaleValuesData = _.filter(
    currentGradeScaleValuesData,
    item => {
      return _.get(item, "type", "REGULAR") !== "SPECIAL";
    }
  );

  let newSpecialValues = [];

  // Add new special grades if template is selected
  if (!_.isEmpty(specialGradesTemplateId)) {
    const specialGradeScaleValuesData = _.get(
      specialGradeScale,
      "gradeScaleEntityDetails[0].gradeScaleValues"
    );
    newSpecialValues = _.map(specialGradeScaleValuesData, item => {
      return {
        type: "SPECIAL",
        id: _.get(item, "id"),
        abbreviation: _.get(item, "abbreviation"),
        label: _.get(item, "label"),
        description: _.get(item, "description"),
        color: _.get(item, "color"),
        presetValueId: _.get(item, "id"),
      };
    });
  }

  const updatedGradeScaleValues = _.isEmpty(newGradeScaleValuesData)
    ? newSpecialValues
    : [...newGradeScaleValuesData, ...newSpecialValues];

  return updateDisplaySequenceForGradeScaleValues(updatedGradeScaleValues);
};

export const getDropdownOptions = ({
  uid,
  options = [],
  gradeScales = [],
  levels = [],
  t,
}) => {
  switch (uid) {
    case "specialGradeTemplate": {
      const gradeScalesTemplate = _.filter(gradeScales, gradeScale =>
        _.includes([SPECIAL], _.get(gradeScale, "node.type"))
      );

      return _.map(gradeScalesTemplate, item => ({
        key: _.get(item, "node.id"),
        value: _.get(item, "node.id"),
        label: _.get(item, "node.templateName"),
      }));
    }

    case "gradeScalesToConvert": {
      const gradeScalesTemplate = _.filter(gradeScales, gradeScale =>
        _.includes(
          [REGULAR, MYP_FINAL_GRADE, DP_IB_DEFINED, MYP_CRITERIA_TOTAL],
          _.get(gradeScale, "node.type")
        )
      );

      return _.map(gradeScalesTemplate, item => ({
        key: _.get(item, "node.id"),
        value: _.get(item, "node.id"),
        label: _.get(item, "node.templateName"),
      }));
    }

    case "selectedLevels": {
      return _.map(levels, level => ({
        key: _.get(level, "id"),
        value: _.get(level, "id"),
        label: _.get(level, "label"),
        subtitle:
          _.get(level, "reportDisplayName") ||
          _.get(level, "abbreviation") ||
          _.get(level, "label"),
      }));
    }

    default: {
      return _.map(options, option => ({
        key: _.get(option, "uid"),
        value: _.get(option, "uid"),
        label: t(_.get(option, "locale_header")),
        subtitle: t(_.get(option, "locale_key")),
      }));
    }
  }
};

export const getSubjectLevelsForGradeScales = ({
  shouldUseGenericTags,
  globalGenericTags,
  levels,
}) => {
  if (shouldUseGenericTags && !_.isEmpty(globalGenericTags)) {
    const dpLevels = _.filter(
      globalGenericTags,
      tag => tag.type === "SUBJECT_LEVEL"
    );

    return _.map(dpLevels, tag => ({
      id: _.get(tag, "id"),
      label: _.get(tag, "label"),
      abbreviation: _.get(tag, "label"),
    }));
  }

  return levels || [];
};
